import {
  Dispatch,
  FC,
  MutableRefObject,
  PropsWithChildren,
  RefObject,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { SelectedLocationContext } from 'contexts/selected-location';
import { getBookingByUnitId, getTransactionById } from 'services';
import {
  ActiveUnit,
  Chassis,
  Container,
  EquipmentType,
  FleetFile,
  Genset,
  Transaction,
  TransactionImage,
  convertSpecialCharacters,
  restoreSpecialCharacters
} from 'types';

import { Loader } from 'components';
import { buildGateUnits } from 'utils';
import { useTransactionApi } from 'hooks/use-transaction-api';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useGateNewBooking } from './booking';
import { useDamage } from './damages';
import { TransactionFailedDialog, TransactionPartialDialog, TransactionSuccessDialog } from './dialogs';
import { GateRedeliveryProvider } from './redelivery';
import GateRedelivery, { GateRedeliveryRef } from './redelivery/gate-redelivery';
import { TireInspection, fromTireInspectionList, useTireInspections } from './tires';
import useReeferBookingAutofill, { ReeferBookingAutofill } from './use-reefer-booking-autofill';

/**
 * A tire position counts as inspected only if the operator recorded something on it.
 *
 * `TireInspections` is a class with twelve initialised fields, one per wheel position, so
 * `Object.values` is always twelve regardless of what was looked at — which meant every gate save
 * posted twelve tire records and stored twelve rows, inspected or not. Axle, centrality and side
 * are the position's identity rather than a finding, so they are excluded from the test; any other
 * field carrying a value counts, including a measurement of zero.
 */
const isInspected = ({ axle, centrality, side, transactionId, ...findings }: TireInspection): boolean =>
  Object.values(findings).some(value => value !== undefined && value !== null && value !== '');

/** `crypto.randomUUID` is unavailable in some test environments; the fallback only needs to be unique. */
const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export enum TransactionStatus {
  DRAFT,
  FAILED,
  /**
   * The transaction saved, but one or more of its uploads did not. Distinct from FAILED on purpose:
   * the record exists, so an operator who reads this as a failed save and re-enters the gate
   * transaction would create a duplicate.
   *
   * **Reachable on the bulk path only.** A single save now sends its attachments in the same
   * request and the server commits all of it or none, so there is no partial outcome left to
   * report. Bulk still fans out because one payload creates N transactions needing the same photos,
   * which the API cannot express in one body yet.
   */
  PARTIAL,
  TRANSACTION,
  WAITING
}

/** What the partial-failure dialog needs to tell the operator. */
export type AttachmentFailure = {
  transactionIds: number[];
  attempted: number;
  failed: number;
  draft: boolean;
};

type NewTransactionContextType = {
  bulk: boolean;
  unstack: boolean;
  setUnstack: Dispatch<SetStateAction<boolean>>;
  chassis: Chassis;
  setChassis: Dispatch<SetStateAction<Chassis>>;
  container: Container;
  setContainer: Dispatch<SetStateAction<Container>>;
  genset: Genset;
  setGenset: Dispatch<SetStateAction<Genset>>;
  images: Partial<TransactionImage> & Required<Pick<TransactionImage, 'imageBase64'>>[];
  addImage: (src: string) => void;
  deleteImage: (src: string) => void;
  stack: Chassis[] | Genset[];
  setStack: Dispatch<SetStateAction<Chassis[] | Genset[]>>;
  status?: TransactionStatus;
  setStatus: Dispatch<SetStateAction<TransactionStatus | undefined>>;
  attachmentFailure?: AttachmentFailure;
  dismissAttachmentFailure: () => void;
  transaction: Partial<Transaction>;
  saveAsDraft: () => void;
  saveTransaction: (signature: string) => any;
  /** Re-submits the save that just failed, unchanged and under the same idempotency key. */
  retrySave: () => void;
  /** Whether there is a failed save to retry, i.e. whether the failure dialog should offer it. */
  canRetry: boolean;
  /** Percentage of the save request sent so far, while one is in flight. */
  uploadProgress?: number;
  updateTransaction: (t: Partial<Transaction>) => void;
  resetTransaction: () => void;
  restoreTransaction: (t: Transaction) => void;
  formRef: RefObject<HTMLFormElement>;
  gateRedeliveryRef: RefObject<GateRedeliveryRef>;
  isEmpty: boolean;
  isTrailer: boolean;
  /**
   * Fleet lookup results for this submission, keyed by unit number + location + equipment type.
   *
   * Previously `Map<string, FleetFile.Unit>` keyed on the bare unit number, which meant a result
   * obtained at one location was served after switching to another, and a `null` miss was cached
   * for the rest of the session. Both are fixed by the composite key and by storing outcomes
   * instead of nullable units — only `found` and `notFound` are ever cached, never a failure.
   *
   * A ref, not state: nothing renders from it, and a caller that captured the lookup function before
   * an answer arrived — the unit field's validation awaits other requests first — must still see
   * that answer. Read from render-time state, it did not, and asked the provider a second time.
   */
  fleetFileUnits: MutableRefObject<Map<string, FleetFile.FleetLookupOutcome>>;
  /**
   * Fleet lookups in flight, under the same key as `fleetFileUnits`. Shared here rather than per hook
   * instance because the unit field and the account field ask for the same unit at the same moment;
   * held per instance, each missed the other's request and the provider was asked twice.
   */
  fleetFileInFlight: MutableRefObject<Map<string, Promise<FleetFile.FleetLookupOutcome>>>;
  interchangeEmails: string;
  setInterchangeEmails: Dispatch<SetStateAction<string>>;
} & ReeferBookingAutofill;

const NewTransactionContext = createContext<NewTransactionContextType>({
  bulk: false,
  unstack: true,
  setUnstack: () => {},
  chassis: new Chassis(),
  setChassis: () => {},
  container: new Container(),
  setContainer: () => {},
  genset: new Genset(),
  setGenset: () => {},
  images: [],
  addImage: () => {},
  deleteImage: () => {},
  stack: [],
  setStack: () => {},
  setStatus: () => {},
  dismissAttachmentFailure: () => {},
  transaction: {} as any,
  saveAsDraft: () => {},
  updateTransaction: () => {},
  saveTransaction: () => 0,
  retrySave: () => {},
  canRetry: false,
  resetTransaction: () => {},
  restoreTransaction: () => {},
  formRef: { current: null },
  gateRedeliveryRef: { current: null },
  isEmpty: true,
  isTrailer: false,
  fleetFileUnits: { current: new Map() },
  fleetFileInFlight: { current: new Map() },
  interchangeEmails: '',
  setInterchangeEmails: () => {},
  reeferInstructions: null,
  applyBookingReefer: () => {},
  releaseReeferField: () => {}
});

export const useNewTransaction = () => useContext(NewTransactionContext);

export const NewTransactionProvider: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { postBulkTransaction, postTransaction, sendInterchange } = useTransactionApi();
  const { setBookings } = useGateNewBooking();

  const formRef = useRef<HTMLFormElement>(null);
  const gateRedeliveryRef = useRef<GateRedeliveryRef>(null);

  /* CONTEXT */
  const { selectedLocation } = useContext(SelectedLocationContext);
  const { damages, resetDamages, restoreDamages } = useDamage();
  const { tires, resetTires, restoreTires, setTriaxle } = useTireInspections();

  /* STATE */

  const [chassis, setChassis] = useState(new Chassis());
  const [container, setContainer] = useState(new Container());
  const [genset, setGenset] = useState(new Genset());
  const [images, setImages] = useState<Partial<TransactionImage> & Required<Pick<TransactionImage, 'imageBase64'>>[]>(
    []
  );
  const [stack, setStack] = useState<Chassis[] | Genset[]>([]);
  const [status, setStatus] = useState<TransactionStatus>();
  const [attachmentFailure, setAttachmentFailure] = useState<AttachmentFailure>();
  /**
   * The key for the save attempt in progress. Minted once per form session and reused by every
   * retry of it, so the server can recognise a retry of a save that in fact committed and answer
   * with the transaction it already created rather than making a second one. Regenerated after a
   * confirmed success, so the next gate move gets a fresh identity.
   */
  const idempotencyKey = useRef<string>(newIdempotencyKey());
  /** The payload of the failed save, held so a retry re-sends it rather than asking for it again. */
  const failedAttempt = useRef<{ transaction: Transaction; unstack: boolean } | undefined>(undefined);
  const [canRetry, setCanRetry] = useState(false);
  /** How much of the save request has been sent, while one is in flight. */
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);
  const [transaction, setTransaction] = useState<Partial<Transaction>>({});
  const [unstack, setUnstack] = useState<boolean>(true);
  const fleetFileUnits = useRef(new Map<string, FleetFile.FleetLookupOutcome>());
  const fleetFileInFlight = useRef(new Map<string, Promise<FleetFile.FleetLookupOutcome>>());
  const [interchangeEmails, setInterchangeEmails] = useState<string>('');
  /**
   * Bumped by restore and reset in the same render as the units they set, so reefer autofill takes what
   * they set as its starting point instead of treating a reopened draft's booking as a new one.
   */
  const [reeferBaseline, setReeferBaseline] = useState(0);
  const reeferAutofill = useReeferBookingAutofill(container, setContainer, reeferBaseline);

  /* MEMOS */

  const bulk = useMemo(() => {
    return searchParams.get('bulk') === 'true';
  }, [searchParams]);

  useEffect(() => setUnstack(searchParams.get('bulk') === 'true'), [searchParams]);

  const getUnits = useCallback((): Partial<Pick<Transaction, 'gateInUnits' | 'gateOutUnits'>> => {
    const unitList = buildGateUnits({ container, genset, stack, chassis, location: selectedLocation });

    return transaction.inbound ? { gateInUnits: unitList } : { gateOutUnits: unitList };
  }, [chassis, container, genset, stack, transaction.inbound, selectedLocation]);

  const convertedDamages: any[] = useMemo(() => {
    return Object.assign([], damages).map(damage => {
      return Object.entries(damage).reduce((acc, [key, val]) => ({ ...acc, [key]: convertSpecialCharacters(val) }), {});
    });
  }, [damages]);

  /* EFFECTS */

  /* keep track of whether draft is empty */
  const isEmpty = useMemo(() => {
    return [
      ...Object.entries(transaction)
        .filter(([k, v]) => !['inbound', 'location'].includes(k))
        .map(([k, v]) => v),
      ...Object.entries(container)
        .filter(([k, v]) => k !== 'equipmentType')
        .map(([k, v]) => v),
      ...Object.entries(chassis)
        .filter(([k, v]) => k !== 'equipmentType')
        .map(([k, v]) => v),
      ...Object.entries(genset)
        .filter(([k, v]) => k !== 'equipmentType')
        .map(([k, v]) => v),
      ...stack,
      ...images
    ].every(el => !el);
  }, [transaction, container, chassis, genset, stack, images]);

  const isTrailer = useMemo(() => !!container.sizeType?.endsWith('TR'), [container.sizeType]);

  /* update tire context on chassisIso change */
  useEffect(() => {
    setTriaxle(chassis.sizeType?.endsWith('TX'));
  }, [chassis.sizeType, setTriaxle]);

  /* handle location change */
  useEffect(() => {
    if (selectedLocation && selectedLocation.id !== transaction.location?.id) {
      setTransaction(t => ({ ...t, location: selectedLocation }));
    }
  }, [selectedLocation, transaction.location, setTransaction]);

  /* handle refresh */
  useEffect(
    () => setTransaction(p => ({ ...p, inbound: searchParams.get('inbound') === 'true' })),
    [searchParams, setTransaction]
  );

  /* confirmation on refresh, navigate */
  useEffect(() => {
    window.onbeforeunload = /new|review/.test(location.pathname) ? () => '' : null;
    return () => {
      window.onbeforeunload = null; /* remove confirm on unmount */
    };
  }, [location.pathname]);

  /* CALLBACKS */

  const restoreTransaction = useCallback(
    async (argT: Transaction) => {
      setStatus(TransactionStatus.WAITING);
      const response = await getTransactionById(argT.id);
      if (!response) {
        setStatus(TransactionStatus.FAILED);
        return;
      }

      const { damages, images, gateInUnits, gateOutUnits, tires, ...t } = { ...response };

      setImages(images);
      setTransaction(t);

      if (damages) {
        restoreDamages(
          damages.map(damage => {
            return Object.entries(damage).reduce((acc, [key, val]) => {
              return { ...acc, [key]: restoreSpecialCharacters(val) };
            }, {});
          })
        );
      }

      if (tires) restoreTires(fromTireInspectionList(tires));

      const units: ActiveUnit[] = Object.assign([], t.inbound ? gateInUnits : gateOutUnits);
      const promises: Promise<any>[] = [];
      units.forEach(async (u, i, arr) => promises.push(getBookingByUnitId(u.id).then(b => (arr[i].booking = b))));
      await Promise.allSettled(promises); /* wait for bookings */

      const chasses = units.filter(u => u.equipmentType === EquipmentType.CHASSIS).map(c => new Chassis(c));
      const gensets = units.filter(u => u.equipmentType === EquipmentType.GENSET).map(g => new Genset(g));
      const containers = units.filter(u => u.equipmentType === EquipmentType.CONTAINER).map(c => new Container(c));

      if (0 in chasses) setChassis(chasses.splice(0, 1)[0]); /* base chassis - always valid (bulk or standard) */

      if (0 in chasses) {
        setStack(chasses); /* chassis stack */
        navigate(`/gate/new?inbound=${!!t.inbound}&bulk=true`);
      } else if (1 in gensets) {
        setStack(gensets); /* genset stack */
        navigate(`/gate/new?inbound=${!!t.inbound}&bulk=true`);
      } else {
        if (0 in gensets) setGenset(gensets[0]); /* single genset */
        if (0 in containers) setContainer(containers[0]); /* single container */
        navigate(`/gate/new?inbound=${!!t.inbound}&bulk=false`);
      }
      setReeferBaseline(b => b + 1); /* same batch as the units: a restored booking is not a new one */
      setStatus(undefined);
    },
    [navigate, restoreDamages, restoreTires, setChassis, setContainer, setGenset, setStack, setStatus, setTransaction]
  );

  const resetTransaction = useCallback(() => {
    setStatus(p => (p !== TransactionStatus.TRANSACTION ? TransactionStatus.WAITING : p));
    resetDamages();
    resetTires();
    setBookings([]);
    setChassis(new Chassis());
    setContainer(new Container());
    setGenset(new Genset());
    setImages([]);
    setStack([]);
    setTransaction({});
    setReeferBaseline(b => b + 1);
    setStatus(p => (p !== TransactionStatus.TRANSACTION ? undefined : p));
    setInterchangeEmails('');
  }, [
    resetDamages,
    resetTires,
    setBookings,
    setChassis,
    setContainer,
    setGenset,
    setImages,
    setStack,
    setStatus,
    setTransaction
  ]);

  const updateTransaction: NewTransactionContextType['updateTransaction'] = useCallback(
    args => setTransaction(readOnly => Object.assign({ ...readOnly }, args)),
    [setTransaction]
  );

  const addImage = useCallback(
    (imageBase64: string) => setImages(readOnly => [...readOnly, { imageBase64 }]),
    [setImages]
  );

  const deleteImage = useCallback(
    (src: string) => setImages(readOnly => readOnly.filter(({ imageBase64 }) => imageBase64 !== src)),
    [setImages]
  );

  /**
   * Submits the save and reports one outcome: it saved, or it did not.
   *
   * There is no third answer any more. Attachments travel in the same request, and the server
   * commits the transaction with all of them or commits nothing — so the partial state this used to
   * report is no longer reachable here. A failure therefore means nothing was written, which is
   * what makes offering a retry safe.
   */
  const submitTransaction = useCallback(
    async (arg: Transaction, unstackArg: boolean) => {
      setStatus(TransactionStatus.WAITING);
      setUploadProgress(bulk ? undefined : 0);

      const response = !bulk
        ? await postTransaction(arg, unstackArg, idempotencyKey.current, setUploadProgress)
        : await postBulkTransaction(arg, unstackArg);

      setUploadProgress(undefined);

      if (!response.ok) {
        /*
         * Nothing was written, so the entered data is still the only copy — hold it, and let the
         * operator either fix it or send it again. Retry is offered only when sending the same
         * thing again could actually work: a rejected unit number or an expired booking will be
         * rejected identically every time, and a retry button there is a loop, not a way out.
         */
        failedAttempt.current = { transaction: arg, unstack: unstackArg };
        setCanRetry(response.retryable);
        setStatus(TransactionStatus.FAILED);
        return;
      }

      failedAttempt.current = undefined;
      setCanRetry(false);

      const saved = 'transactions' in response ? response.transactions : [response.transaction];

      if ('attachments' in response && response.attachments.failed) {
        /* Bulk only: it still fans out, so it can still land partially. See TransactionStatus. */
        setAttachmentFailure({
          transactionIds: saved.map(({ id }) => id).filter((id): id is number => !!id),
          attempted: response.attachments.attempted,
          failed: response.attachments.failed,
          draft: false
        });
        setStatus(TransactionStatus.PARTIAL);
        return;
      }

      /*
       * Interchange emails go out only now, after the save is confirmed. They used to be fired
       * before the outcome was known, which would have told a customer about a gate move that then
       * failed to save — and an email cannot be recalled.
       */
      saved.forEach(({ id }) =>
        interchangeEmails.split(',').forEach(email => {
          id && email.length && sendInterchange(email, id);
        })
      );

      /* This save is finished; the next transaction must not be mistaken for a retry of it. */
      idempotencyKey.current = newIdempotencyKey();

      setStatus(TransactionStatus.TRANSACTION);
      navigate('/gate');
    },
    [bulk, interchangeEmails, navigate, postBulkTransaction, postTransaction, sendInterchange, setStatus]
  );

  const saveTransaction: NewTransactionContextType['saveTransaction'] = useCallback(
    async signature => {
      const arg: Transaction = {
        damages: convertedDamages,
        images: [{ imageBase64: signature }, ...images].map((image, index) => ({ ...image, index })),
        location: selectedLocation,
        tires: Object.values(tires).filter(isInspected),
        ...transaction,
        ...getUnits(),
        draft: false
      } as any;

      await submitTransaction(arg, unstack);
    },
    [convertedDamages, images, selectedLocation, tires, transaction, unstack, getUnits, submitTransaction]
  );

  /**
   * Re-sends the failed save exactly as it was, under the same idempotency key. If the original
   * attempt actually committed and only its response was lost, the server answers with that
   * transaction instead of creating a second one.
   */
  const retrySave = useCallback(() => {
    const attempt = failedAttempt.current;
    if (!attempt) return;
    submitTransaction(attempt.transaction, attempt.unstack);
  }, [submitTransaction]);

  const saveAsDraft = useCallback(async () => {
    setStatus(TransactionStatus.WAITING);

    /*
     * No idempotency key on a draft. A draft is saved over and over as the operator works, and a
     * key would make every save after the first replay the first one.
     */
    const response = await postTransaction({
      damages: convertedDamages,
      images,
      location: selectedLocation,
      tires: Object.values(tires).filter(isInspected),
      ...transaction,
      ...getUnits(),
      draft: true
    } as any);

    if (!response.ok) {
      /*
       * No retry on a draft. Drafts carry no idempotency key — deliberately, since a draft is saved
       * repeatedly as the operator works — so a retry after a lost response would create a second
       * draft rather than returning the first.
       */
      setCanRetry(false);
      setStatus(TransactionStatus.FAILED);
      return;
    }

    await restoreTransaction(response.transaction);

    setStatus(TransactionStatus.DRAFT);
  }, [
    convertedDamages,
    images,
    selectedLocation,
    tires,
    transaction,
    getUnits,
    postTransaction,
    restoreTransaction,
    setStatus
  ]);

  /**
   * Acknowledging the partial failure resumes the navigation that was held back so the dialog
   * could be read. A draft stays on the page, as saving a draft never navigated away.
   */
  const dismissAttachmentFailure = useCallback(() => {
    const wasDraft = attachmentFailure?.draft;
    setAttachmentFailure(undefined);
    setStatus(undefined);
    if (!wasDraft) navigate('/gate');
  }, [attachmentFailure, navigate, setStatus]);

  return (
    <NewTransactionContext.Provider
      value={{
        bulk,
        unstack,
        setUnstack,
        chassis,
        setChassis,
        container,
        setContainer,
        genset,
        setGenset,
        images,
        addImage,
        deleteImage,
        stack,
        setStack,
        status,
        setStatus,
        attachmentFailure,
        dismissAttachmentFailure,
        transaction,
        saveAsDraft,
        saveTransaction,
        retrySave,
        canRetry,
        uploadProgress,
        updateTransaction,
        resetTransaction,
        restoreTransaction,
        formRef,
        gateRedeliveryRef,
        isEmpty,
        isTrailer,
        fleetFileUnits,
        fleetFileInFlight,
        interchangeEmails,
        setInterchangeEmails,
        ...reeferAutofill
      }}
    >
      <Loader
        id="review-loader"
        loading={status === TransactionStatus.WAITING}
        label={uploadProgress === undefined ? undefined : `Uploading ${uploadProgress}%`}
      >
        <GateRedeliveryProvider>
          {children}
          <GateRedelivery ref={gateRedeliveryRef} />
        </GateRedeliveryProvider>
      </Loader>
      <TransactionFailedDialog />
      <TransactionPartialDialog />
      <TransactionSuccessDialog />
    </NewTransactionContext.Provider>
  );
};
